// src/components/survey/ParticipantBulkInsertDialog.js

import { useNavigate } from 'react-router-dom'; // 2. Import useParams and useNavigate
import React, { useState } from 'react';
import { LoaderCircle, Upload, Download, FileText, AlertCircle } from 'lucide-react'; // For icons

// Import your reusable UI components
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '../ui/Dialog';
import Button from '../ui/Button';

import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';

import API_CONFIG from '../../config/api'
const BASE_URL = API_CONFIG.BASE_URL


/**
 * A dialog component for bulk inserting participant tokens from CSV.
 * It handles file upload, validation rules display, and template download.
 *
 * @param {object} props
 * with the uploaded file upon successful submission.
 */
export default function ParticipantBulkInsertDialog({ surveyId }) {
	const navigate = useNavigate(); // Initialize useNavigate hook
	// State to control the dialog's open/closed status
	const [isOpen, setIsOpen] = useState(false);

	// State for the form input
	const [selectedFile, setSelectedFile] = useState(null);
	
	// State for API call status
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [uploadResult, setUploadResult] = useState(null);

	const { authenticatedFetch } = useAuthenticatedApi();

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			// Validate file type
			if (!file.name.toLowerCase().endsWith('.csv')) {
				setError('Please select a CSV file.');
				setSelectedFile(null);
				return;
			}
			setSelectedFile(file);
			setError(null);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!selectedFile) {
			setError('Please select a CSV file.');
			return;
		}

		setIsLoading(true);
		setError(null);
		setUploadResult(null);

		try {
			const formData = new FormData();
			formData.append('file', selectedFile);
			const material = {
				// we dont want to stringify FormData
				body: formData,
			};
			const endpoint = BASE_URL + `/active_surveys/create-participant-token/bulk-insert/${surveyId}`
			const result = await authenticatedFetch(endpoint, material, 'POST');
			if (result.status === 404) {
				navigate('/404', { state: { message: `Survey with ID "${surveyId}" not found in database.` } });
				return;
			}
			if (!result.ok) {
				// Handle other non-2xx status codes (e.g., 500, 401, 403)
				let errorMessage = `Failed to fetch survey: Server responded with status ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
					// Ignore if response body isn't JSON
				}
				console.error("GenerateTokenForm API Error:", errorMessage, result);
				setError(errorMessage);
				return; // Stop further execution
			}
			const datas = await result.json();
			
			// Set the upload result to display in the insert log
			setUploadResult(datas);
			
			// Reset file selection but keep the dialog open to show results
			setSelectedFile(null);
		} catch (err) {
			setError(err.message || 'Failed to process file. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const downloadTemplate = () => {
		// Create CSV content with headers and sample data
		const csvContent = `name,max_use,valid_from,valid_until,description
token_alpha,10,2024-01-01,2025-01-01,Token for alpha testing group
token_beta,5,2024-02-01T09:00:00,2024-12-31T23:59:59,Token for beta testing group
token_gamma,1,,,"Single use token for special access"`;

		// Create and download the file
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', 'token_template.csv');
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};
	
	// When the dialog's open state changes, reset the error message
	const handleOpenChange = (open) => {
		if (!open) {
			setSelectedFile(null);
			setError(null);
			setUploadResult(null);
		}
		setIsOpen(open);
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button
					variant="primary"
					className="flex gap-2 items-center px-3 py-1.5 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
				>
					<Upload className='h-4 w-4'/> Bulk insert
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[750px]">
				<DialogHeader>
					<DialogTitle className="text-primary">Bulk Insert Participant Tokens</DialogTitle>
					<DialogDescription>
						Upload a CSV file to create multiple participant tokens at once.
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="py-4">
					<div className="grid gap-6">
						{/* File Upload Section */}
						<div className="grid gap-2">
							<label htmlFor="csvFile" className="font-semibold text-primary">
								CSV File
							</label>
							<input
								id="csvFile"
								type="file"
								accept=".csv"
								onChange={handleFileChange}
								className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
								disabled={isLoading}
							/>
							{selectedFile && (
								<p className="text-sm text-gray-600 flex items-center gap-2">
									<FileText className="h-4 w-4" />
									Selected: {selectedFile.name}
								</p>
							)}
						</div>

						{/* Template Download */}
						<div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
							<div className="flex items-center gap-2">
								<Download className="h-4 w-4 text-gray-600" />
								<span className="text-sm text-gray-700">Need a template?</span>
							</div>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={downloadTemplate}
								className="text-sm"
							>
								Download Template
							</Button>
						</div>

						{/* CSV Format Rules */}
						<div className="border border-gray-200 rounded-md p-4 bg-gray-50">
							<h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
								<AlertCircle className="h-4 w-4" />
								CSV Format Requirements
							</h3>
							<div className="space-y-3 text-sm">
								<div>
									<h4 className="font-medium text-gray-900 mb-1">Required Columns:</h4>
									<ul className="list-disc list-inside text-gray-700 space-y-1">
										<li><strong>name:</strong> Unique identifier for the token (1-255 characters)</li>
										<li><strong>max_use:</strong> Maximum number of times the token can be used (must be `&gt;` 0)</li>
									</ul>
								</div>
								<div>
									<h4 className="font-medium text-gray-900 mb-1">Optional Columns:</h4>
									<ul className="list-disc list-inside text-gray-700 space-y-1">
										<li><strong>valid_from:</strong> Start date (ISO format: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS, defaults to today)</li>
										<li><strong>valid_until:</strong> End date (ISO format: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS, defaults to 1 year from today)</li>
										<li><strong>description:</strong> Optional description (max 1000 characters)</li>
									</ul>
								</div>
								<div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
									<p className="text-blue-800 text-xs">
										<strong>Note:</strong> Date fields use Europe/Paris timezone. Leave empty for defaults.
										The valid_until date must be after valid_from date.
									</p>
								</div>
							</div>
						</div>
						
						{/* Insert Log Results */}
						{uploadResult && (
							<div className="border border-gray-200 rounded-md p-4 bg-gray-50">
								<h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
									<FileText className="h-4 w-4" />
									Insert Log
								</h3>
								<div className="space-y-2 text-sm">
									<div className="grid grid-cols-3 gap-4">
										<div className="text-center">
											<div className="font-medium text-gray-900">Total</div>
											<div className="text-lg font-bold text-blue-600">{uploadResult.total_processed}</div>
										</div>
										<div className="text-center">
											<div className="font-medium text-gray-900">Success</div>
											<div className="text-lg font-bold text-green-600">{uploadResult.successful_inserts}</div>
										</div>
										<div className="text-center">
											<div className="font-medium text-gray-900">Failed</div>
											<div className="text-lg font-bold text-red-600">{uploadResult.failed_inserts}</div>
										</div>
									</div>
									
									{uploadResult.failed_inserts > 0 && (
										<>
											<hr className="my-3 border-gray-300" />
											<div>
												<h4 className="font-medium text-gray-900 mb-2">Error Details:</h4>
												<div className="space-y-1 max-h-32 overflow-y-auto">
													{Object.entries(uploadResult.errors_dict).map(([rowId, errorMessage]) => (
														<div key={rowId} className="text-xs p-2 bg-red-50 border border-red-200 rounded">
															<span className="font-medium text-red-800">Row {rowId}:</span>{' '}
															<span className="text-red-700">{errorMessage}</span>
														</div>
													))}
												</div>
											</div>
										</>
									)}
									
									{uploadResult.successful_inserts > 0 && (
										<div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
											✓ {uploadResult.message}
										</div>
									)}
								</div>
							</div>
						)}
						
						{error && (
							<div className="p-3 bg-red-50 border border-red-200 rounded-md">
								<p className="text-sm text-red-600 flex items-center gap-2">
									<AlertCircle className="h-4 w-4" />
									{error}
								</p>
							</div>
						)}
					</div>

					<DialogFooter className="pt-8">
						<Button 
							type="button" 
							variant="outline" 
							onClick={() => setIsOpen(false)}
							disabled={isLoading}
						>
							{uploadResult ? 'Close' : 'Cancel'}
						</Button>
						{!uploadResult && (
							<Button 
								type="submit" 
								disabled={isLoading || !selectedFile}
								className="ml-2"
							>
								{isLoading && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
								Upload CSV
							</Button>
						)}
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}